import { useMemo } from "react";
import { useSelector } from "react-redux";

import { Observation } from "@medplum/fhirtypes";

import { List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getBirthingRowValues } from "@/encounters/components/EncounterLabor/utils/labor";
import { selectBirthDateTime } from "@/store/slices/birth";
import { localTimeFromISO } from "@/utils/datetime";

interface BirthingRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  flowsheetId?: string;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

export default function BirthingRow({
  time,
  observation,
  showNumbersOnly,
  flowsheetId,
  showPreviousVersions = true,
  isViewOnly = false,
}: BirthingRowProps) {
  const birthDateTime = useSelector(selectBirthDateTime);
  const {
    crownDelivered,
    headDelivered,
    bodyDelivered,
    type,
    position,
    inWater,
    fetalPresentation,
    deliveryMethod,
    notes,
    hasAnswers,
    hasNotes,
  } = useMemo(() => getBirthingRowValues(observation), [observation]);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  const previousVersions = hasPreviousVersions(observation);
  const isPostpartumFlowsheet =
    flowsheetId === "clientPostpartum" || flowsheetId === "newbornPostpartum";
  const isRowViewOnly = isViewOnly || isPostpartumFlowsheet;

  // Align the row timestamp with the canonical birth date/time when set (not the birthing
  // Observation.effectiveDateTime from the original form save).
  const displayTime =
    showPreviousVersions && birthDateTime ? localTimeFromISO(birthDateTime) : time;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell
          rowSpan={1}
          time={displayTime}
          observation={observation}
          isViewOnly={isRowViewOnly}
        />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Birthing:</TextWrapper>
            <List fz="sm" withPadding>
              {crownDelivered && (
                <List.Item>
                  <strong>Crown Delivered:</strong> {crownDelivered}
                </List.Item>
              )}
              {headDelivered && (
                <List.Item>
                  <strong>Head Delivered:</strong> {headDelivered}
                </List.Item>
              )}
              {bodyDelivered && (
                <List.Item>
                  <strong>Body Delivered:</strong> {bodyDelivered}
                </List.Item>
              )}
              {type && (
                <List.Item>
                  <strong>Type:</strong> {type}
                </List.Item>
              )}
              {position && (
                <List.Item>
                  <strong>Position:</strong> {position}
                </List.Item>
              )}
              {inWater && (
                <List.Item>
                  <strong>Client in Water:</strong> {inWater}
                </List.Item>
              )}
              {fetalPresentation && (
                <List.Item>
                  <strong>Fetal Presentation:</strong> {fetalPresentation}
                </List.Item>
              )}
              {deliveryMethod && (
                <List.Item>
                  <strong>Delivery Method:</strong> {deliveryMethod}
                </List.Item>
              )}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Birthing Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={BirthingRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {!isRowViewOnly && showPreviousVersions && (
        <ActionCell rowSpan={1} observation={observation} />
      )}
    </Table.Tr>
  );
}
